import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2, Pencil, Upload, X, Check, Palette } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { PLATFORM_META, type Platform } from '../lib/platforms';
import { AccountAvatar } from '../components/AccountAvatar';

type Brand = {
  id: string;
  name: string;
  description: string | null;
  logoUrl: string | null;
  colors: string[];
  accountIds: string[];
  createdAt: string;
  updatedAt: string;
};

type ConnectedAccount = {
  id: string;
  platform: Platform;
  username: string | null;
  displayName: string | null;
  profilePhotoUrl: string | null;
  status: 'connected' | 'disconnected';
  connectedAt: string;
  lastSyncedAt: string;
};

const PRESET_COLORS = [
  '#0F172A', '#1E293B', '#475569', '#94A3B8',
  '#EF4444', '#F97316', '#F59E0B', '#EAB308',
  '#84CC16', '#22C55E', '#10B981', '#14B8A6',
  '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1',
  '#8B5CF6', '#A855F7', '#D946EF', '#EC4899',
];

export function Brand() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Brand | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const brandsQuery = useQuery({
    queryKey: ['brands'],
    queryFn: () => api<{ brands: Brand[] }>('/api/brands'),
  });

  const accountsQuery = useQuery({
    queryKey: ['social', 'accounts'],
    queryFn: () => api<{ accounts: ConnectedAccount[] }>('/api/social/accounts'),
  });

  const brands = brandsQuery.data?.brands ?? [];
  const accounts = accountsQuery.data?.accounts ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/api/brands/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['brands'] }),
  });

  return (
    <>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Palette size={22} className="text-brand-600" /> Tu marca</h1>
          <p className="mt-2 text-slate-600 max-w-2xl">
            Define cómo es tu marca y vincúlala a tus cuentas. La IA usará esta información para crear
            posts coherentes con tu identidad.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary gap-2">
          <Plus size={16} /> Nueva marca
        </button>
      </div>

      <section className="mt-8">
        {brandsQuery.isLoading ? (
          <div className="card p-6 text-slate-500 flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Cargando…
          </div>
        ) : brands.length === 0 ? (
          <div className="card p-8 text-center text-slate-500">
            <p>Aún no tienes ninguna marca creada.</p>
            <button onClick={() => setShowCreate(true)} className="btn-primary gap-2 mt-4 mx-auto">
              <Plus size={16} /> Crear mi primera marca
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {brands.map((b) => {
              const brandAccounts = accounts.filter((a) => b.accountIds.includes(a.id));
              return (
                <div key={b.id} className="card flex flex-col gap-4">
                  <div className="flex items-start gap-4">
                    <BrandLogo logo={b.logoUrl} name={b.name} colors={b.colors} />
                    <div className="flex-1 min-w-0">
                      <h2 className="font-semibold text-slate-900 truncate">{b.name}</h2>
                      <p className="text-sm text-slate-500 line-clamp-2 mt-0.5">
                        {b.description ?? 'Sin descripción.'}
                      </p>
                      {b.colors.length > 0 && (
                        <div className="flex gap-1.5 mt-2">
                          {b.colors.map((c) => (
                            <span
                              key={c}
                              title={c}
                              className="h-5 w-5 rounded-full border border-slate-200"
                              style={{ backgroundColor: c }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <button onClick={() => setEditing(b)} className="btn-secondary p-2" title="Editar">
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`¿Eliminar la marca "${b.name}"? Las cuentas vinculadas se desvincularán.`)) {
                            deleteMutation.mutate(b.id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        className="btn-secondary p-2 text-red-600 hover:bg-red-50"
                        title="Eliminar"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="border-t border-slate-100 pt-3">
                    <div className="text-xs font-medium text-slate-500 mb-2">
                      {brandAccounts.length === 0
                        ? 'Sin cuentas vinculadas'
                        : `${brandAccounts.length} cuenta${brandAccounts.length > 1 ? 's' : ''} vinculada${brandAccounts.length > 1 ? 's' : ''}`}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {brandAccounts.map((a) => (
                        <div
                          key={a.id}
                          className="inline-flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-2 py-1"
                        >
                          <AccountAvatar account={a} size={20} />
                          <span className="text-xs text-slate-700 max-w-[140px] truncate">
                            {a.displayName ?? a.username ?? PLATFORM_META[a.platform].label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {(showCreate || editing) && (
        <BrandModal
          brand={editing}
          allBrands={brands}
          accounts={accounts}
          onClose={() => {
            setShowCreate(false);
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

/** Returns perceived luminance 0–255 from a hex color string. */
function hexLuminance(hex: string): number {
  const c = hex.replace('#', '');
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/** Picks the darkest brand color; falls back to slate-900 if all are light or none exist. */
function logoBg(colors: string[]): string {
  const valid = colors.filter((c) => /^#[0-9a-fA-F]{6}$/.test(c));
  if (valid.length === 0) return '#0F172A';
  const darkest = valid.reduce((a, b) => (hexLuminance(a) <= hexLuminance(b) ? a : b));
  // If the darkest color is still very light, fall back to a dark slate
  return hexLuminance(darkest) > 180 ? '#0F172A' : darkest;
}

function BrandLogo({ logo, name, colors }: { logo: string | null; name: string; colors: string[] }) {
  if (logo) {
    const bg = logoBg(colors);
    return (
      <div
        className="h-14 w-14 rounded-xl flex items-center justify-center overflow-hidden shrink-0"
        style={{ backgroundColor: bg }}
      >
        <img src={logo} alt={name} className="max-h-12 max-w-12 object-contain" />
      </div>
    );
  }
  const bg = colors[0] ?? '#0F172A';
  return (
    <div
      className="h-14 w-14 rounded-xl flex items-center justify-center text-white font-bold text-lg shrink-0"
      style={{ backgroundColor: bg }}
    >
      {name.slice(0, 2).toUpperCase()}
    </div>
  );
}

function BrandModal({
  brand,
  allBrands,
  accounts,
  onClose,
}: {
  brand: Brand | null;
  allBrands: Brand[];
  accounts: ConnectedAccount[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = brand !== null;
  const [name, setName] = useState(brand?.name ?? '');
  const [description, setDescription] = useState(brand?.description ?? '');
  const [colors, setColors] = useState<string[]>(brand?.colors ?? []);
  const [logoUrl, setLogoUrl] = useState<string | null>(brand?.logoUrl ?? null);
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(
    new Set(brand?.accountIds ?? []),
  );
  const [logoUploading, setLogoUploading] = useState(false);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [pendingLogoPreview, setPendingLogoPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Accounts already taken by *other* brands (not editable)
  const takenAccountIds = useMemo(() => {
    const set = new Set<string>();
    for (const b of allBrands) {
      if (brand && b.id === brand.id) continue;
      for (const id of b.accountIds) set.add(id);
    }
    return set;
  }, [allBrands, brand]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        description: description.trim() ? description : null,
        colors,
        logoUrl,
      };

      let saved: Brand;
      if (isEdit) {
        const res = await api<{ brand: Brand }>(`/api/brands/${brand!.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        saved = res.brand;
      } else {
        const res = await api<{ brand: Brand }>('/api/brands', {
          method: 'POST',
          body: JSON.stringify(body),
        });
        saved = res.brand;

        // Upload pending logo now that we have a brand ID
        if (pendingLogoFile) {
          try {
            const publicUrl = await uploadLogoFile(pendingLogoFile, saved.id);
            await api(`/api/brands/${saved.id}`, {
              method: 'PATCH',
              body: JSON.stringify({ logoUrl: publicUrl }),
            });
          } catch {
            // Non-fatal: brand is created, logo upload failed silently
          }
        }
      }

      // Sync account links
      await api(`/api/brands/${saved.id}/accounts`, {
        method: 'PUT',
        body: JSON.stringify({ accountIds: Array.from(selectedAccounts) }),
      });

      return saved;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) setError(err.message);
      else setError('No se pudo guardar la marca.');
    },
  });

  async function uploadLogoFile(file: File, brandId: string): Promise<string> {
    const { uploadUrl, publicUrl } = await api<{ uploadUrl: string; publicUrl: string }>(
      `/api/brands/${brandId}/logo-upload-url`,
      {
        method: 'POST',
        body: JSON.stringify({ mimeType: file.type, sizeBytes: file.size }),
      },
    );
    const put = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type, 'Cache-Control': 'public, max-age=31536000' },
      body: file,
    });
    if (!put.ok) throw new Error('Upload failed');
    return publicUrl;
  }

  async function handleLogoFile(file: File) {
    setError(null);
    if (!isEdit) {
      // In create mode: store file locally and show a preview — upload happens on save
      setPendingLogoFile(file);
      setPendingLogoPreview(URL.createObjectURL(file));
      return;
    }
    setLogoUploading(true);
    try {
      const publicUrl = await uploadLogoFile(file, brand!.id);
      setLogoUrl(publicUrl);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('No se pudo subir el logo.');
    } finally {
      setLogoUploading(false);
    }
  }

  function toggleAccount(id: string) {
    if (takenAccountIds.has(id)) return;
    setSelectedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function addColor(color: string) {
    const c = color.toUpperCase();
    if (!/^#([0-9A-F]{3}|[0-9A-F]{6})$/.test(c)) return;
    if (colors.includes(c)) return;
    if (colors.length >= 3) return;
    setColors([...colors, c]);
  }

  function removeColor(c: string) {
    setColors(colors.filter((x) => x !== c));
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900">
            {isEdit ? 'Editar marca' : 'Nueva marca'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="card border-red-200 bg-red-50">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Nombre *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              placeholder="Mi marca"
              className="input w-full"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Logo (recomendado PNG transparente)
            </label>
            <div className="flex items-center gap-3">
              {(logoUrl || pendingLogoPreview) ? (
                <div
                  className="h-16 w-16 rounded-xl flex items-center justify-center overflow-hidden shrink-0"
                  style={{ backgroundColor: logoBg(colors) }}
                >
                  <img src={pendingLogoPreview ?? logoUrl!} alt="Logo" className="max-h-14 max-w-14 object-contain" />
                </div>
              ) : (
                <div className="h-16 w-16 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0">
                  <span className="text-xs text-slate-400">Sin logo</span>
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleLogoFile(f);
                      e.target.value = '';
                    }}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={logoUploading}
                    className="btn-secondary gap-2"
                  >
                    {logoUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {(logoUrl || pendingLogoPreview) ? 'Cambiar logo' : 'Subir logo'}
                  </button>
                  {(logoUrl || pendingLogoPreview) && (
                    <button
                      onClick={() => { setLogoUrl(null); setPendingLogoFile(null); setPendingLogoPreview(null); }}
                      className="btn-secondary text-red-600"
                    >
                      Quitar
                    </button>
                  )}
                </div>
                {!isEdit && pendingLogoFile && (
                  <p className="text-xs text-brand-600">
                    El logo se subirá al crear la marca.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Colores de la marca (máx. 3)
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {colors.map((c) => (
                <div
                  key={c}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 pl-1 pr-2 py-1"
                >
                  <span
                    className="h-5 w-5 rounded-full border border-slate-200"
                    style={{ backgroundColor: c }}
                  />
                  <span className="text-xs font-mono text-slate-600">{c}</span>
                  <button onClick={() => removeColor(c)} className="text-slate-400 hover:text-red-600">
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
            {colors.length < 3 && <ColorPicker onPick={addColor} />}
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Descripción de la marca
            </label>
            <textarea
              value={description ?? ''}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={8000}
              rows={8}
              placeholder="Cuéntanos a qué se dedica tu marca, qué la hace única, tu tono de voz, valores, audiencia objetivo, no hacer/sí hacer… Cuanto más detallado, mejores resultados generará la IA."
              className="input w-full resize-y"
            />
            <p className="text-xs text-slate-500 mt-1">
              {description?.length ?? 0} / 8000 caracteres
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Vincular cuentas
            </label>
            <p className="text-xs text-slate-500 mb-2">
              Una cuenta solo puede pertenecer a una marca.
            </p>
            <div className="space-y-2">
              {accounts.length === 0 ? (
                <p className="text-sm text-slate-500">No tienes cuentas conectadas todavía.</p>
              ) : (
                accounts.map((a) => {
                  const taken = takenAccountIds.has(a.id);
                  const selected = selectedAccounts.has(a.id);
                  return (
                    <button
                      key={a.id}
                      onClick={() => toggleAccount(a.id)}
                      disabled={taken}
                      className={
                        'w-full flex items-center gap-3 rounded-lg border px-3 py-2 text-left transition ' +
                        (taken
                          ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                          : selected
                          ? 'border-brand-500 bg-brand-50'
                          : 'border-slate-200 bg-white hover:border-slate-300')
                      }
                    >
                      <AccountAvatar account={a} size={32} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {a.displayName ?? a.username ?? '—'}
                        </div>
                        <div className="text-xs text-slate-500">
                          {PLATFORM_META[a.platform].label}
                          {taken && <span className="ml-2 text-amber-600">ya pertenece a otra marca</span>}
                        </div>
                      </div>
                      {selected && !taken && <Check size={18} className="text-brand-600 shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={!name.trim() || saveMutation.isPending}
            className="btn-primary gap-2"
          >
            {saveMutation.isPending && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? 'Guardar cambios' : 'Crear marca'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ColorPicker({ onPick }: { onPick: (color: string) => void }) {
  const [hex, setHex] = useState('#');
  const [pickerColor, setPickerColor] = useState('#3B82F6');

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-10 gap-1.5">
        {PRESET_COLORS.map((c) => (
          <button
            key={c}
            onClick={() => onPick(c)}
            className="h-7 w-7 rounded-md border border-slate-200 hover:scale-110 transition"
            style={{ backgroundColor: c }}
            title={c}
          />
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={pickerColor}
          onChange={(e) => setPickerColor(e.target.value)}
          className="h-9 w-12 rounded-md border border-slate-200 cursor-pointer"
        />
        <button onClick={() => onPick(pickerColor)} className="btn-secondary text-sm">
          Añadir
        </button>
        <div className="text-xs text-slate-400">o</div>
        <input
          type="text"
          value={hex}
          onChange={(e) => setHex(e.target.value)}
          placeholder="#RRGGBB"
          maxLength={7}
          className="input text-sm font-mono flex-1"
        />
        <button
          onClick={() => {
            onPick(hex);
            setHex('#');
          }}
          className="btn-secondary text-sm"
          disabled={!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)}
        >
          Añadir hex
        </button>
      </div>
    </div>
  );
}
